import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { ACH_PROXY_PREFIX } from '../constants/ach/proxy';
import {
  achApiHostMap,
  apiHostMap,
  geolinkAudienceCreationServiceApiHostMap,
  graphQLApiHostMap,
  poiManagementApiHostMap,
} from '../constants/config';
import {
  achBackendEnabledDefault,
  AchDomain,
  apiHostFor as resolveApiHostFor,
  effectiveAchBackendEnabled,
  resolveMigratedDomains,
} from '../util/apiHostFor';
import { isLocalhostUrl } from '../util/helpers';
import { useLocalStorage } from './useLocalStorage';

// ConfigProvider is the outer provider (AuthProvider nests in it), so it reads
// the user email from localStorage rather than useAuth to avoid the cycle.
const readCurrentUserEmail = (): string | undefined => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw)?.email : undefined;
  } catch {
    return undefined;
  }
};

type ConfigContextType = {
  apiHost: any;
  setApiHost: any;
  useUPViews: boolean;
  setUseUPViews: (value: boolean) => void;
  graphQLApiHost: any;
  setGraphQLApiHost: any;
  poiManagementApiHost: any;
  setPoiManagementApiHost: any;
  geolinkAudienceCreationServiceApiHost: any;
  setGeolinkAudienceCreationServiceApiHost: any;
  achApiHost: any;
  setAchApiHost: any;
  // Raw toggle state for the tester UI only. Routing decisions must use
  // apiHostFor, which applies the allowlist gate; this field does not.
  achBackendEnabled: boolean;
  setAchBackendEnabled: (value: boolean) => void;
  apiHostFor: (domain: AchDomain) => string;
};

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

// The four backend environments the host maps are keyed by. In local dev the
// active env is chosen declaratively via the VITE_API_ENV .env var (replacing the
// old interactive DebugView host-switcher): set it to one of these and every
// service resolves to that env's host. Invalid/unset => fall back to the existing
// hostname-sniffing defaults below (which is also what deployed builds use, since
// VITE_API_ENV is a dev-only convenience and is unset in prod bundles).
const API_ENVS = ['development', 'sandbox', 'qa', 'production'] as const;
type ApiEnv = (typeof API_ENVS)[number];

// selectedApiEnv reads VITE_API_ENV, but ONLY honors it in a DEV build — a prod
// bundle must keep resolving hosts by window.location.hostname (see getDefault*).
const selectedApiEnv = (): ApiEnv | undefined => {
  if (!import.meta.env.DEV) return undefined;
  const raw = import.meta.env.VITE_API_ENV;
  return (API_ENVS as readonly string[]).includes(raw) ? (raw as ApiEnv) : undefined;
};

type ConfigProviderProps = {
  children: React.ReactNode;
  optionalInitialHost?: string;
};

export function ConfigProvider({ children, optionalInitialHost }: ConfigProviderProps) {
  const getDefaultApiHost = () => {
    if (optionalInitialHost) {
      return optionalInitialHost;
    }
    const env = selectedApiEnv();
    if (env) {
      return apiHostMap[env];
    }
    if (import.meta.env.DEV && isLocalhostUrl(window.location.hostname)) {
      return apiHostMap.sandbox;
    }
    if (
      import.meta.env.PROD &&
      !window.location.hostname.includes('-qa.') &&
      !window.location.hostname.includes('staging.') &&
      !window.location.hostname.startsWith('dev.')
    ) {
      return apiHostMap.production;
    }
    return apiHostMap.qa;
  };

  const getDefaultGraphQLApiHost = () => {
    if (optionalInitialHost) {
      return optionalInitialHost;
    }
    const env = selectedApiEnv();
    if (env) {
      return graphQLApiHostMap[env];
    }
    if (import.meta.env.DEV && isLocalhostUrl(window.location.hostname)) {
      return graphQLApiHostMap.sandbox;
    }
    if (
      import.meta.env.PROD &&
      !window.location.hostname.includes('-qa.') &&
      !window.location.hostname.includes('staging.') &&
      !window.location.hostname.startsWith('dev.')
    ) {
      return graphQLApiHostMap.production;
    }
    return graphQLApiHostMap.qa;
  };

  const getDefaultPoiManagementApiHost = () => {
    if (optionalInitialHost) {
      return optionalInitialHost;
    }
    const env = selectedApiEnv();
    if (env) {
      return poiManagementApiHostMap[env];
    }
    if (import.meta.env.DEV && isLocalhostUrl(window.location.hostname)) {
      return poiManagementApiHostMap.sandbox;
    }
    if (
      import.meta.env.PROD &&
      !window.location.hostname.includes('-qa.') &&
      !window.location.hostname.includes('staging.') &&
      !window.location.hostname.startsWith('dev.')
    ) {
      return poiManagementApiHostMap.production;
    }
    return poiManagementApiHostMap.qa;
  };

  const getDefaultGeolinkAudienceCreationServiceApiHost = () => {
    if (optionalInitialHost) {
      return optionalInitialHost;
    }
    const env = selectedApiEnv();
    if (env) {
      return geolinkAudienceCreationServiceApiHostMap[env];
    }
    if (import.meta.env.DEV && isLocalhostUrl(window.location.hostname)) {
      return geolinkAudienceCreationServiceApiHostMap.sandbox;
    }
    if (
      import.meta.env.PROD &&
      !window.location.hostname.includes('-qa.') &&
      !window.location.hostname.includes('staging.') &&
      !window.location.hostname.startsWith('dev.')
    ) {
      return geolinkAudienceCreationServiceApiHostMap.production;
    }
    return geolinkAudienceCreationServiceApiHostMap.qa;
  };

  const getDefaultAchApiHost = () => {
    if (optionalInitialHost) {
      return optionalInitialHost;
    }
    // Local dev: route ACH through the same-origin `/ach` proxy whenever the
    // selected env's ACH host is REMOTE — we can't add the localhost origin to a
    // remote host's CORS allowlist. If that host is one we run locally
    // (localhost), call it directly; so `sandbox` (localhost:8080) hits the local
    // ACH server with no proxy, while `development` (the CircleCI-deployed ACH dev
    // host) goes through the proxy. VITE_API_ENV defaults to `sandbox` when unset
    // (matching the other getDefault* services and the proxy target default in
    // vite.config.ts — both must agree).
    if (import.meta.env.DEV && isLocalhostUrl(window.location.hostname)) {
      const achHost = achApiHostMap[selectedApiEnv() ?? 'sandbox'];
      return isLocalhostUrl(achHost) ? achHost : ACH_PROXY_PREFIX;
    }
    if (
      import.meta.env.PROD &&
      !window.location.hostname.includes('-qa.') &&
      !window.location.hostname.includes('staging.') &&
      !window.location.hostname.startsWith('dev.')
    ) {
      return achApiHostMap.production;
    }
    return achApiHostMap.qa;
  };

  const [apiHost, setApiHost] = useLocalStorage('apiHost', getDefaultApiHost());
  const [useUPViews, setUseUPViews] = useLocalStorage('useUPViews', false);
  const [graphQLApiHost, setGraphQLApiHost] = useLocalStorage('graphQLApiHost', getDefaultGraphQLApiHost());
  const [poiManagementApiHost, setPoiManagementApiHost] = useLocalStorage(
    'poiManagementApiHost',
    getDefaultPoiManagementApiHost()
  );
  const [geolinkAudienceCreationServiceApiHost, setGeolinkAudienceCreationServiceApiHost] = useLocalStorage(
    'geolinkAudienceCreationServiceApiHost',
    getDefaultGeolinkAudienceCreationServiceApiHost()
  );
  // Tier-B env gate: default ON only in QA (design §3.2), OFF elsewhere;
  // localStorage-overridable per session so a domain can be flip-tested against
  // ACH in prod. Mirrors the getDefault* env-sniffing pattern above.
  const getDefaultAchBackendEnabled = () => {
    const env = selectedApiEnv();
    if (env) {
      return achBackendEnabledDefault(env);
    }
    if (import.meta.env.DEV && isLocalhostUrl(window.location.hostname)) {
      return achBackendEnabledDefault('sandbox');
    }
    if (
      import.meta.env.PROD &&
      !window.location.hostname.includes('-qa.') &&
      !window.location.hostname.includes('staging.') &&
      !window.location.hostname.startsWith('dev.')
    ) {
      return achBackendEnabledDefault('production');
    }
    return achBackendEnabledDefault('qa');
  };

  const [achApiHost, setAchApiHost] = useLocalStorage('achApiHost', getDefaultAchApiHost());
  const achEnvDefault = getDefaultAchBackendEnabled();
  const [achBackendEnabled, setAchBackendEnabled] = useLocalStorage('achBackendEnabled', achEnvDefault);
  // TEST-ONLY (e2e/) flip seam: a localStorage array that overrides the
  // checked-in ACH_MIGRATED_DOMAINS so the live-test harness can flip a domain
  // ON for one run without a rebuild. resolveMigratedDomains ignores it outside
  // DEV builds, so production resolution is unchanged. Default null => no override.
  const [migratedDomainsOverride] = useLocalStorage('e2eMigratedDomains', null);

  const apiHostFor = useCallback(
    (domain: AchDomain) =>
      resolveApiHostFor(domain, {
        apiHost,
        achApiHost,
        achBackendEnabled: effectiveAchBackendEnabled({
          isDev: import.meta.env.DEV,
          storedEnabled: achBackendEnabled,
          envDefault: achEnvDefault,
          userEmail: readCurrentUserEmail(),
          allowlistRaw: import.meta.env.VITE_ACH_TESTER_EMAILS,
        }),
        migratedDomains: resolveMigratedDomains(migratedDomainsOverride, import.meta.env.DEV),
      }),
    [apiHost, achApiHost, achBackendEnabled, achEnvDefault, migratedDomainsOverride]
  );

  React.useEffect(() => {
    if (
      import.meta.env.PROD &&
      geolinkAudienceCreationServiceApiHost !== geolinkAudienceCreationServiceApiHostMap.production
    ) {
      setGeolinkAudienceCreationServiceApiHost(geolinkAudienceCreationServiceApiHostMap.production);
    }
  }, [geolinkAudienceCreationServiceApiHost, setGeolinkAudienceCreationServiceApiHost]);

  // Local dev: VITE_API_ENV (resolved through getDefault*) is the single source
  // of truth for which backend env every service points at — this replaces the
  // removed interactive DebugView host-switcher. Because the hosts are persisted
  // in localStorage, a value stored from a previous session would otherwise
  // shadow a changed VITE_API_ENV, so reset each host to its env-derived default
  // whenever a stored value drifts. Gated to DEV: prod builds resolve hosts by
  // window.location.hostname and must NOT be overridden here (the separate PROD
  // effect above owns geolink).
  React.useEffect(() => {
    if (!import.meta.env.DEV) return;
    const entries: Array<[string, string, (value: string) => void]> = [
      [apiHost, getDefaultApiHost(), setApiHost],
      [graphQLApiHost, getDefaultGraphQLApiHost(), setGraphQLApiHost],
      [poiManagementApiHost, getDefaultPoiManagementApiHost(), setPoiManagementApiHost],
      [
        geolinkAudienceCreationServiceApiHost,
        getDefaultGeolinkAudienceCreationServiceApiHost(),
        setGeolinkAudienceCreationServiceApiHost,
      ],
      [achApiHost, getDefaultAchApiHost(), setAchApiHost],
    ];
    entries.forEach(([current, next, set]) => {
      if (current !== next) set(next);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiHost, graphQLApiHost, poiManagementApiHost, geolinkAudienceCreationServiceApiHost, achApiHost]);

  const value = useMemo(
    () => ({
      apiHost,
      setApiHost,
      useUPViews,
      setUseUPViews,
      graphQLApiHost,
      setGraphQLApiHost,
      poiManagementApiHost,
      setPoiManagementApiHost,
      geolinkAudienceCreationServiceApiHost,
      setGeolinkAudienceCreationServiceApiHost,
      achApiHost,
      setAchApiHost,
      achBackendEnabled,
      setAchBackendEnabled,
      apiHostFor,
    }),
    [
      apiHost,
      setApiHost,
      useUPViews,
      setUseUPViews,
      graphQLApiHost,
      setGraphQLApiHost,
      poiManagementApiHost,
      setPoiManagementApiHost,
      geolinkAudienceCreationServiceApiHost,
      setGeolinkAudienceCreationServiceApiHost,
      achApiHost,
      setAchApiHost,
      achBackendEnabled,
      setAchBackendEnabled,
      apiHostFor,
    ]
  );

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export const useConfig = (): ConfigContextType => useContext(ConfigContext);
